import api from './api';
import { auth } from '../firebase';

/**
 * Scenario Modeling service — thin wrapper around the scenario endpoints:
 *   GET  /api/v1/scenarios/actuals
 *   POST /api/v1/scenarios/{scenarioType}/project
 *
 * Auth/401 handling mirrors the rest of the CFO Insights surface: on 401 we
 * sign out via Firebase so the existing App.js -> /login flow fires, then
 * re-throw so callers can surface an error state in the brief window before
 * the auth state change unmounts the page.
 *
 * orgId is resolved server-side from the authenticated Firebase UID.
 */

function handleAuthError(error: unknown): void {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 401) {
    auth.signOut();
  }
}

/* ── Request / response shapes ──────────────────────────────────────────── */

export interface ScenarioAssumptions {
  winRate: number;              // 0–1 fraction
  grossMarginAssumption: number; // 0–1 fraction
  paymentTermsDays: number;      // integer days
  retainagePct: number;          // 0–1 fraction
  pipelineAwardsValue: number;   // USD
  // V1 "add a specific project" workflow (spec §11.B). Request-scoped only —
  // never persisted. Omit or set null for no added project.
  addedProject?: AddedProjectRequest | null;
}

/**
 * Single project input for the "Add a Project" workflow. Dates are ISO
 * yyyy-MM-dd strings. Only one added project is supported per V1 (spec §13).
 */
export interface AddedProjectRequest {
  projectName: string;
  projectType?: string | null;
  contractValue: number;
  grossMarginEstimate: number;   // 0–1 fraction
  startDate: string;             // ISO date
  endDate?: string | null;       // ISO date; provide this or durationMonths
  durationMonths?: number | null;
  paymentTermsDays: number;
  apTimingDays: number;
  retainagePct: number;          // 0–1 fraction
  materialDepositAmount?: number | null;
  bondingRequirement?: number | null;
  capitalRequirement?: number | null;
  confidencePct?: number | null; // 0–1 fraction, defaults to 1.0 server-side
}

/** A numeric output paired with its data-integrity source label (spec §11.D). */
export interface LabeledValue {
  value?: number | null;
  source?: 'source_backed' | 'customer_assumption' | 'constructpay_projection' | 'missing_data' | 'policy_required' | string;
  note?: string | null;
}

export interface AddedProjectImpact {
  projectName?: string;
  combinedCashCurve?: number[];
  cashTrough?: LabeledValue;
  cashTroughMonth?: string;
  workingCapitalImpact?: LabeledValue;
  capitalNeed?: LabeledValue;
  capitalRequirementEntered?: LabeledValue;
  apPressure?: LabeledValue;
  retainageImpact?: LabeledValue;
  bondingDiscussionArea?: LabeledValue;
  assumptionsUsed?: string[];
}

export interface ProjectionKpis {
  daysCashOnHand?: number;
  currentRatio?: number;
  grossMarginPct?: number;
  workingCapitalGap?: number;
  /** Lowest point of cashCurve, before any LOC draw. */
  projectedCashLow?: number;
  /** Undrawn LOC headroom applied against projectedCashLow. */
  availableLocHeadroom?: number;
  projectedRevenue12Mo?: number;
  retainageLockup?: number;
}

export interface CapitalRecommendation {
  facilityType?: string;
  recommendedAmount?: number;
  rationale?: string;
}

export interface RiskFlag {
  level?: string;
  message?: string;
}

export interface PartnerProfile {
  engagementUrgency?: string;
  lendingOpportunity?: string;
  underwritingRisk?: string;
  pipelineProjects?: string;
}

export interface ProjectionResponse {
  kpis?: ProjectionKpis;
  cashCurve?: number[];
  projectionMonths?: string[];
  capitalRecommendations?: CapitalRecommendation[];
  riskFlags?: RiskFlag[];
  partnerProfile?: PartnerProfile;
  // Optional per-scenario editor defaults; populated by backend if available.
  defaults?: Partial<ScenarioAssumptions> & Record<string, number>;
  // Present only when the request included an added project.
  addedProjectImpact?: AddedProjectImpact | null;
}

export interface ActualsResponse {
  // Anchored-to-actuals card fields. Types are loose because the backend may
  // return raw USD numbers or pre-formatted strings; callers format as needed.
  startingCash?: number | string;
  activeWip?: number | string;
  netUnderbilling?: number | string;
  locFacility?: string;
  retainageHeld?: number | string;
  asOfDate?: string;
  // Data-integrity labels ("source_backed" | "missing_data") so the UI never displays an
  // unmapped/null field as a fabricated $0.
  activeWipValueSource?: string;
  netUnderbillingSource?: string;
  locFacilitySource?: string;
  retainageSource?: string;
  // Optional pre-computed projections so the page can render without a
  // second round-trip on load. May include just "base" or all scenarios.
  scenarios?: Record<string, ProjectionResponse>;
  // Some backends return the base projection at the top level instead of
  // nesting it under `scenarios`; we honor either shape.
  baseScenario?: ProjectionResponse;
  [key: string]: unknown;
}

/* ── Calls ──────────────────────────────────────────────────────────────── */

/**
 * GET /api/v1/scenarios/actuals
 *
 * Returns current-period actuals for the Anchored-to-Actuals card. Response
 * may also include a pre-computed base-case (and optionally growth/stress)
 * projection so the page can render the default view without a second call.
 */
export async function getActuals(): Promise<ActualsResponse> {
  try {
    const { data } = await api.get<ActualsResponse>('/v1/scenarios/actuals');
    return data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
}

/**
 * POST /api/v1/scenarios/{scenarioType}/project
 *
 * All five assumptions are sent as-is; callers are responsible for converting
 * % inputs to 0–1 fractions and $M to USD before calling.
 */
export async function projectScenario(
  scenarioType: string,
  assumptions: ScenarioAssumptions
): Promise<ProjectionResponse> {
  try {
    const { data } = await api.post<ProjectionResponse>(
      `/v1/scenarios/${scenarioType}/project`,
      assumptions
    );
    return data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
}
